/**
 * Freedom Fuel — Voting (storefront bridge)
 *
 * Powers two pieces of the Shopify-native voting UI:
 *   1. [data-my-votes]      — live spendable vote balance for the logged-in customer
 *   2. [data-ff-vote-cast]  — "Cast a Vote" buttons (spend votes on a hero)
 *
 * Authenticated calls route through a Shopify App Proxy (window.FFVotingConfig.proxyBase):
 *   GET  {base}/my-votes  -> { balance }            customer's spendable balance
 *   POST {base}/vote      -> { ok, balance, hero }  cast votes for a hero
 * The proxy injects logged_in_customer_id server-side, so we never send a customer id.
 *
 * Graceful degradation while the proxy/endpoints are not yet live:
 *   - balance keeps its cached optimistic value (never crashes)
 *   - Cast buttons fall back to the cart-drawer hero allocator (window.FFVoting.focusHero),
 *     then finally to the button's href — never a self-link reload.
 *
 * Config + this script are emitted by snippets/ff-voting.liquid.
 */
(function () {
  if (window.__ffVotingInit) return; // idempotent — snippet may render per-section
  window.__ffVotingInit = true;

  const cfg = window.FFVotingConfig || {};
  const BASE = String(cfg.proxyBase || '/apps/ff-voting').replace(/\/+$/, '');
  const LS_BALANCE = 'ff_my_votes';

  ready(init);

  function init() {
    bindCast();
    refreshBalance();
    syncHeroTotals();
  }

  /* ───────────────────────── Balance ───────────────────────── */

  function balanceEls() {
    return document.querySelectorAll('[data-my-votes]');
  }

  function refreshBalance() {
    if (!balanceEls().length) return;

    // Instant optimistic paint from cache (e.g. set on a previous page).
    try {
      const cached = localStorage.getItem(LS_BALANCE);
      if (cached != null && cached !== '') renderBalance(cached);
    } catch (_) {}

    // A guest has no balance to fetch — leave the optimistic/zero value.
    if (!cfg.loggedIn) return;

    fetch(`${BASE}/my-votes`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const bal = pickBalance(d);
        if (bal == null) return;
        renderBalance(bal);
        try { localStorage.setItem(LS_BALANCE, String(bal)); } catch (_) {}
      })
      .catch(() => {
        /* proxy not live yet / not signed in — keep the optimistic value */
      });
  }

  function renderBalance(n) {
    const text = formatBalance(n);
    balanceEls().forEach((el) => {
      el.textContent = text;
      el.classList.add('is-loaded');
    });
  }

  function formatBalance(n) {
    const v = Math.max(0, Math.floor(Number(n) || 0));
    return v < 10 ? '0' + v : String(v); // match the "00" two-digit widget style
  }

  function currentBalance() {
    const el = balanceEls()[0];
    if (el) {
      const v = parseInt(String(el.textContent).replace(/[^\d]/g, ''), 10);
      if (!isNaN(v)) return v;
    }
    try {
      const c = parseInt(localStorage.getItem(LS_BALANCE) || '', 10);
      if (!isNaN(c)) return c;
    } catch (_) {}
    return 0;
  }

  function adjustBalance(delta) {
    const next = Math.max(0, currentBalance() + delta);
    renderBalance(next);
    try { localStorage.setItem(LS_BALANCE, String(next)); } catch (_) {}
  }

  function pickBalance(d) {
    if (d == null) return null;
    if (typeof d === 'number') return d;
    const keys = ['balance', 'votes', 'myVotes', 'voteBalance', 'available', 'remaining'];
    for (const k of keys) if (typeof d[k] === 'number') return d[k];
    if (d.data) return pickBalance(d.data);
    return null;
  }

  /* ───────────────────── Live hero totals (read) ───────────────────── */
  // Hero list/single pages render vote counts from the static metaobject
  // `vote_count` field (server-side Liquid). The app is the source of truth for
  // live totals (legacy baseline + votes cast through the app, including order
  // auto-casts), so fetch them and patch the numbers + share bars on load and
  // after a cast. Degrades silently to the server-rendered values on any error.

  function hasHeroDisplays() {
    return !!document.querySelector('.ff-hna__card-progress-num, .ff-hns__tally-num');
  }

  function numericId(gid) {
    const m = String(gid || '').match(/(\d+)\s*$/);
    return m ? m[1] : '';
  }

  function syncHeroTotals() {
    if (!hasHeroDisplays()) return;

    fetch(`${BASE}/heroes`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const list = (d && (d.heroes || d.data)) || (Array.isArray(d) ? d : []);
        if (!Array.isArray(list) || !list.length) return;

        const byId = new Map();
        let total = 0;
        for (const h of list) {
          const id = numericId(h.id || h.handle);
          // `votes` = canonical display total (baseline + live casts); fall back to votesCast.
          const v = Math.max(0, Number(h.votes != null ? h.votes : h.votesCast) || 0);
          if (id) byId.set(id, v);
          total += v;
        }
        applyHeroTotals(byId, total);
      })
      .catch(() => {
        /* proxy/endpoint not live — keep the server-rendered numbers */
      });
  }

  function applyHeroTotals(byId, total) {
    // Archive cards — scope each update to its own hero card.
    document.querySelectorAll('.ff-hna__card-n').forEach((card) => {
      const btn = card.querySelector('[data-ff-vote-hero-id]');
      const id = numericId(btn && btn.getAttribute('data-ff-vote-hero-id'));
      if (!id || !byId.has(id)) return;

      const votes = byId.get(id);
      const numEl = card.querySelector('.ff-hna__card-progress-num');
      if (numEl) numEl.textContent = String(votes);

      const share = total > 0 ? (votes * 100) / total : 0;
      const shareEl = card.querySelector('.ff-hna__card-share');
      if (shareEl) shareEl.textContent = share.toFixed(1) + '%';
      const bar = card.querySelector('.ff-hna__card-progress-track > span');
      if (bar) bar.style.width = Math.min(share * 1.5, 100) + '%';
    });

    // Single hero page — one nominee; match by the hero id present on the page.
    document.querySelectorAll('[data-ff-vote-hero-id]').forEach((btn) => {
      const id = numericId(btn.getAttribute('data-ff-vote-hero-id'));
      if (!id || !byId.has(id)) return;
      document.querySelectorAll('.ff-hns__tally-num').forEach((el) => {
        el.textContent = String(byId.get(id));
      });
    });
  }

  /* ───────────────────────── Cast a vote ───────────────────────── */

  function bindCast() {
    // Capture phase: we run before (and suppress) the cart-drawer's bubble handler
    // on the same [data-ff-vote-hero-id] element, and before any self-link navigation.
    document.addEventListener('click', onCastClick, true);
  }

  function onCastClick(e) {
    const btn = e.target.closest('[data-ff-vote-cast]');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    castVote(btn);
  }

  function castVote(btn) {
    if (btn.dataset.ffBusy === '1') return;

    const heroId = btn.getAttribute('data-ff-vote-hero-id') || btn.getAttribute('data-ff-vote-cast') || '';
    const qty = Math.max(1, parseInt(btn.getAttribute('data-ff-vote-qty') || '1', 10) || 1);
    const name = btn.getAttribute('data-ff-vote-name') || 'this hero';

    // The proxy can only attribute votes to a logged-in customer.
    if (!cfg.loggedIn) {
      const ret = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${cfg.loginUrl || '/account/login'}?return_url=${ret}`;
      return;
    }

    setBusy(btn, true);

    fetch(`${BASE}/cast-votes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ heroId: heroId, votes: qty }),
    })
      .then(async (r) => {
        let data = null;
        try { data = await r.json(); } catch (_) {}
        if (!r.ok) {
          const err = new Error('cast-failed');
          err.status = r.status;
          err.data = data;
          throw err;
        }
        return data || {};
      })
      .then((data) => onCastSuccess(btn, { heroId, qty, name, data }))
      .catch((err) => onCastError(btn, { heroId, qty, name, err }))
      .finally(() => setBusy(btn, false));
  }

  function onCastSuccess(btn, ctx) {
    const { name, qty, heroId, data } = ctx;

    const bal = pickBalance(data);
    if (bal != null) {
      renderBalance(bal);
      try { localStorage.setItem(LS_BALANCE, String(bal)); } catch (_) {}
    } else {
      adjustBalance(-qty); // optimistic decrement when the API omits the new balance
    }

    bumpHeroTally(heroId, qty, data); // instant optimistic paint
    syncHeroTotals(); // then reconcile every card against the server's authoritative totals
    toast(`${qty === 1 ? 'Vote' : qty + ' votes'} cast for ${name}. Thank you!`, 'ok');
  }

  function onCastError(btn, ctx) {
    const { name, heroId, err } = ctx;
    const status = err && err.status;
    const code = err && err.data && (err.data.error || err.data.code);

    // Customer is known but out of votes — point them at earning more.
    if (status === 402 || status === 409 || code === 'insufficient_votes' || code === 'insufficient_balance') {
      toast(`You're out of votes. Earn more by shopping, then vote for ${name}.`, 'warn');
      return;
    }

    // Session expired / proxy says not authenticated — send to login.
    if (status === 401 || status === 403) {
      const ret = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${cfg.loginUrl || '/account/login'}?return_url=${ret}`;
      return;
    }

    // Endpoint not live yet (404), network error, etc. — fall back to the
    // existing cart-drawer hero allocator so the click still does something useful.
    if (window.FFVoting && typeof window.FFVoting.focusHero === 'function' && heroId) {
      window.FFVoting.focusHero(heroId);
      return;
    }

    // Last resort: follow the button's real href (earn/cart), not a self-link reload.
    const href = ctx && getHref(heroId);
    if (href) window.location.href = href;
    else toast('Could not cast your vote right now. Please try again.', 'warn');
  }

  function getHref(heroId) {
    const btn = document.querySelector(`[data-ff-vote-cast][data-ff-vote-hero-id="${cssEscape(heroId)}"]`);
    const href = btn && btn.getAttribute('href');
    if (href && href !== '#' && !href.startsWith('javascript:')) return href;
    return null;
  }

  /* Optimistic hero tally bump — best effort across both layouts. */
  function bumpHeroTally(heroId, qty, data) {
    const apiVotes = pickHeroVotes(data);

    // Archive cards: scope to the card that owns this hero.
    document.querySelectorAll(`[data-ff-vote-hero-id="${cssEscape(heroId)}"]`).forEach((b) => {
      const card = b.closest('.ff-hna__card-n');
      if (card) setNum(card.querySelector('.ff-hna__card-progress-num'), apiVotes, qty);
    });

    // Single hero page: only one nominee on the page.
    document.querySelectorAll('.ff-hns__tally-num').forEach((el) => setNum(el, apiVotes, qty));
  }

  function pickHeroVotes(d) {
    if (d == null) return null;
    const hero = d.hero || d.nominee || d;
    const keys = ['votesCast', 'vote_count', 'votes', 'total'];
    for (const k of keys) if (typeof hero[k] === 'number') return hero[k];
    return null;
  }

  function setNum(el, apiVal, qty) {
    if (!el) return;
    if (apiVal != null) {
      el.textContent = String(apiVal);
      return;
    }
    const cur = parseInt(String(el.textContent).replace(/[^\d]/g, ''), 10);
    el.textContent = String((isNaN(cur) ? 0 : cur) + qty);
  }

  /* ───────────────────────── UI helpers ───────────────────────── */

  function setBusy(btn, busy) {
    btn.dataset.ffBusy = busy ? '1' : '0';
    btn.classList.toggle('is-ff-voting', busy);
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) {
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.7';
    } else {
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
    }
  }

  let toastEl = null;
  let toastTimer = null;
  function toast(msg, kind) {
    ensureToastStyles();
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ff-vote-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.dataset.kind = kind || 'ok';
    // reflow so the transition re-runs
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3600);
  }

  function ensureToastStyles() {
    if (document.getElementById('ff-vote-toast-styles')) return;
    const style = document.createElement('style');
    style.id = 'ff-vote-toast-styles';
    style.textContent = `
      .ff-vote-toast{position:fixed;left:50%;bottom:24px;transform:translate(-50%,16px);
        z-index:9999;max-width:min(92vw,420px);padding:14px 18px;
        background:#141414;color:#fff;border:1px solid rgba(255,255,255,.14);
        border-left:3px solid #C8102E;border-radius:8px;
        font-family:'Thedus Condensed Light','Oswald','Arial Narrow',sans-serif;
        font-size:14px;letter-spacing:.04em;line-height:1.4;
        box-shadow:0 12px 36px rgba(0,0,0,.45);
        opacity:0;pointer-events:none;transition:opacity .22s ease,transform .22s ease;}
      .ff-vote-toast.is-visible{opacity:1;transform:translate(-50%,0);}
      .ff-vote-toast[data-kind="warn"]{border-left-color:#E6A23A;}
      .ff-vote-toast[data-kind="ok"]{border-left-color:#3AC07A;}`;
    document.head.appendChild(style);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function cssEscape(str) {
    return String(str).replace(/(["\\\]\[])/g, '\\$1');
  }
})();
